import React, { useContext } from 'react';
import { AgFinancingFilesContext, AgFinancingPhase3Status, ContractStatus, Phase3 } from '@indigo-ag/schema';
import { Body, LegalBody, palettes, ReadOnlyField, TinyTitle } from '@indigo-ag/component-module';

import i18next from 'core/i18n';
import {
  NotificationSection,
  NotificationSectionProps
} from 'features/brazil-grower/components/BrazilGrowerFinance/shared/NotificationSection/NotificationSection';
import {
  DocumentCard,
  documentRequestTypesTranslations,
  EnhancedExpansionPanel,
  phase3FileTypes,
  text,
  UserUtil,
  utils,
  Permissions,
  createStylesHook
} from 'shared';
import { UserContext } from 'core/components/GateWay/GateWay';
import { DocumentsValidationList } from '../DocumentsValidationSection/DocumentsValidationList/DocumentsValidationList';
import { DocumentsValidationCreditAnalystAndManagerDecisions } from './DocumentsValidationCreditAnalystAndManagerDecisions/DocumentsValidationCreditAnalystAndManagerDecisions';

import ConditionalsAndExceptions from './ConditionalsAndExceptions/ConditionalsAndExceptions';

interface ContractStateProps {
  phase3Info: Phase3;
}

const useStyles = createStylesHook({
  boldComment: {
    display: 'block',
    fontSize: '0.875rem !important',
    'font-weight': '700 !important',
    paddingBottom: '0.25rem ',
    margin: '0 !important',
    color: `${palettes.brand.gray['700']} !important`
  }
});

export const ContractState: React.FunctionComponent<ContractStateProps> = ({ phase3Info }) => {
  const {
    signedDocumentsReceivedAt,
    signedDocumentsReviewedAt,
    signedDocumentsReviewedBy,
    signedDocumentsReviewNotes,
    amendmentCreationNotes,
    startingAmendmentCreationAt,
    startingAmendmentCreationBy,
    amendmentSignatureRequestedAt,
    amendmentSignatureRequestedBy,
    amendmentSignatureRequestedNotes,
    amendmentRegistrationNumber,
    amendmentInstructions,
    amendmentInformationUpdatedAt,
    amendmentSignedAt,
    amendmentSignedBy,
    amendmentSignedNotes,
    amendmentTrackingNumber,
    contractStatus,
    files,
    draftInstructions,
    draftRegistrationNumber,
    draftSentAt,
    draftSentBy,
    status,
    startedAt,
    startedBy
  } = phase3Info;

  /**
   * User Context
   */
  const { isCreditLead, isCreditAnalyst, isInternal, isSuper, isLuchesi, hasPermission } = useContext(UserContext);
  const isSecretary = hasPermission(Permissions.FA_SECRETARY);

  /**
   * Styles
   */
  const classes = useStyles();

  const getNotificationProps: () => NotificationSectionProps = () => {
    if (status === AgFinancingPhase3Status.ON_HOLD) {
      return {
        title: `${i18next.t('phase')} 3 ${i18next.t('onHold')}`,
        status: 'warning',
        updateBy: UserUtil.getUserFullName(signedDocumentsReviewedBy)
      };
    }

    switch (contractStatus) {
      case ContractStatus.WAITING_DRAFT:
        return {
          title: i18next.t('waitingDraftGeneration'),
          subtitle: i18next.t('waitingDraftGenerationSubtitle'),
          status: 'warning',
          updatedAt: startedAt,
          updateBy: UserUtil.getUserFullName(startedBy)
        };

      case ContractStatus.DRAFT_SENT_TO_COMMERCIAL:
        return {
          title: i18next.t('waitingNotarizedDocuments'),
          subtitle: amendmentTrackingNumber
            ? i18next.t('waitingNotarizedDocumentsSubtitleAmendment')
            : i18next.t('waitingNotarizedDocumentsSubtitle'),
          status: 'warning',
          updatedAt: amendmentTrackingNumber ? amendmentSignedAt : draftSentAt,
          updateBy: UserUtil.getUserFullName(amendmentTrackingNumber ? amendmentSignedBy : draftSentBy)
        };

      case ContractStatus.REVIEWING_SIGNED_DOCUMENTS:
        return {
          title: i18next.t('waitingSignedDocumentsValidation'),
          status: 'warning',
          updatedAt: signedDocumentsReviewedAt || signedDocumentsReceivedAt,
          updateBy:
            UserUtil.getUserFullName(signedDocumentsReviewedBy) ||
            utils.text.firstLetterUppercase(i18next.t('documentsReceivedAt'))
        };

      case ContractStatus.SIGNED_DOCUMENTS_SENT_TO_CREDIT_TEAM:
        return {
          title: i18next.t('signedDocumentsSentToCreditTeam'),
          status: 'warning',
          updatedAt: signedDocumentsReviewedAt,
          updateBy: UserUtil.getUserFullName(signedDocumentsReviewedBy)
        };

      case ContractStatus.SIGNED_DOCUMENTS_SENT_TO_CREDIT_MANAGER:
        return {
          title: i18next.t('signedDocumentsSentToCreditManager'),
          status: 'warning',
          updatedAt: signedDocumentsReviewedAt,
          updateBy: UserUtil.getUserFullName(signedDocumentsReviewedBy)
        };

      case ContractStatus.SIGNED_DOCUMENTS_DENIED:
        return {
          title: i18next.t('signedDocumentsDenied'),
          status: 'error',
          updatedAt: signedDocumentsReviewedAt,
          updateBy: UserUtil.getUserFullName(signedDocumentsReviewedBy)
        };

      case ContractStatus.SIGNED_DOCUMENTS_APPROVED:
        return {
          title: i18next.t('signedDocumentsApproved'),
          status: 'success',
          updatedAt: signedDocumentsReviewedAt,
          updateBy: UserUtil.getUserFullName(signedDocumentsReviewedBy)
        };

      case ContractStatus.CREATING_AMENDMENT:
        return {
          title: i18next.t('waitingAmendment'),
          status: 'warning',
          updatedAt: amendmentInformationUpdatedAt || startingAmendmentCreationAt,
          updateBy: !amendmentInformationUpdatedAt ? UserUtil.getUserFullName(startingAmendmentCreationBy) : undefined
        };

      case ContractStatus.WAITING_DIGITAL_SIGNATURE:
      case ContractStatus.WAITING_PHYSICAL_SIGNATURE:
        return {
          title: i18next.t(
            contractStatus === ContractStatus.WAITING_DIGITAL_SIGNATURE
              ? 'waitingDigitalSignature'
              : 'waitingPhysicalSignature'
          ),
          status: 'warning',
          updatedAt: amendmentSignatureRequestedAt,
          updateBy: UserUtil.getUserFullName(amendmentSignatureRequestedBy)
        };

      default:
        return {
          title: '',
          status: 'warning'
        };
    }
  };

  const draftFiles = files?.filter(f => f.context === AgFinancingFilesContext.CONTRACT_DRAFT_DOCUMENTS) || [];
  const amendmentFiles = files?.filter(f => f.context === AgFinancingFilesContext.AMENDMENT_DOCUMENTS) || [];

  const commentsOrRecommendations =
    contractStatus === ContractStatus.DRAFT_SENT_TO_COMMERCIAL && draftInstructions && !isLuchesi
      ? amendmentTrackingNumber
        ? `${i18next.t('trackingNumber')}: ${amendmentTrackingNumber}${
            amendmentSignedNotes ? ` - ${amendmentSignedNotes}` : ''
          }`
        : draftInstructions
      : contractStatus &&
        [
          ContractStatus.REVIEWING_SIGNED_DOCUMENTS,
          ContractStatus.SIGNED_DOCUMENTS_DENIED,
          ContractStatus.SIGNED_DOCUMENTS_APPROVED,
          ContractStatus.SIGNED_DOCUMENTS_SENT_TO_CREDIT_TEAM,
          ContractStatus.SIGNED_DOCUMENTS_SENT_TO_CREDIT_MANAGER
        ].includes(contractStatus) &&
        signedDocumentsReviewNotes
      ? signedDocumentsReviewNotes
      : contractStatus === ContractStatus.CREATING_AMENDMENT && amendmentCreationNotes
      ? amendmentCreationNotes
      : contractStatus &&
        [ContractStatus.WAITING_DIGITAL_SIGNATURE, ContractStatus.WAITING_PHYSICAL_SIGNATURE].includes(
          contractStatus
        ) &&
        amendmentSignatureRequestedNotes
      ? amendmentSignatureRequestedNotes
      : '';

  const showDocumentsValidationSection =
    contractStatus && ![ContractStatus.WAITING_DRAFT, ContractStatus.DRAFT_SENT_TO_COMMERCIAL].includes(contractStatus);

  const showActionsForInternalUsers =
    status === AgFinancingPhase3Status.IN_PROGRESS &&
    showDocumentsValidationSection &&
    contractStatus &&
    (([
      ContractStatus.SIGNED_DOCUMENTS_SENT_TO_CREDIT_MANAGER,
      ContractStatus.SIGNED_DOCUMENTS_SENT_TO_CREDIT_TEAM
    ].includes(contractStatus) &&
      isSuper) ||
      (isCreditLead && contractStatus === ContractStatus.SIGNED_DOCUMENTS_SENT_TO_CREDIT_MANAGER) ||
      (isCreditAnalyst && contractStatus === ContractStatus.SIGNED_DOCUMENTS_SENT_TO_CREDIT_TEAM));

  const showAmendmentFilesSection =
    contractStatus &&
    (contractStatus === ContractStatus.CREATING_AMENDMENT ||
      !!files?.filter(f => f.context === AgFinancingFilesContext.AMENDMENT_DOCUMENTS).length);

  return (
    <NotificationSection {...getNotificationProps()}>
      {!!commentsOrRecommendations.length && (
        <>
          <LegalBody className={classes.boldComment}>{i18next.t('commentsOrRecommendations')}</LegalBody>
          <Body className="mt-1 break-line">{commentsOrRecommendations || ' - '}</Body>
        </>
      )}
      {(isCreditLead || isInternal) && (
        <>
          <div className="mt-4">
            {!isSecretary ? (
              <EnhancedExpansionPanel title={i18next.t('draftFiles')}>
                <div>
                  <ReadOnlyField label={i18next.t('registrationNumber')} value={draftRegistrationNumber || '-'} />
                  {contractStatus !== ContractStatus.DRAFT_SENT_TO_COMMERCIAL && draftInstructions ? (
                    <>
                      <LegalBody className={classes.boldComment}>{i18next.t('commentsOrRecommendations')}</LegalBody>
                      <Body className="mt-1 break-line">{draftInstructions || ' - '}</Body>
                    </>
                  ) : null}
                </div>
              </EnhancedExpansionPanel>
            ) : null}
            {showDocumentsValidationSection && !isSecretary && (
              <EnhancedExpansionPanel title={i18next.t('documentsValidation')}>
                <DocumentsValidationList phase3Info={phase3Info} />
              </EnhancedExpansionPanel>
            )}
            {showAmendmentFilesSection && (
              <EnhancedExpansionPanel title={i18next.t('amendmentFiles')}>
                <div>
                  <ReadOnlyField
                    label={text.firstLetterUppercase(i18next.t('amendmentRegistrationNumber'))}
                    value={amendmentRegistrationNumber || '-'}
                  />
                  {contractStatus !== ContractStatus.CREATING_AMENDMENT && amendmentInstructions ? (
                    <ReadOnlyField
                      label={text.firstLetterUppercase(i18next.t('instructions'))}
                      value={amendmentInstructions}
                    />
                  ) : null}
                  <div className="flex flex-column">
                    {amendmentFiles?.map(file => (
                      <div key={file.id} className="flex flex-column">
                        <TinyTitle className="text-transform-capitalize" style={{ marginBottom: 4 }}>
                          {documentRequestTypesTranslations[file.documentType]}
                        </TinyTitle>
                        <DocumentCard document={file} />
                      </div>
                    ))}
                  </div>
                </div>
              </EnhancedExpansionPanel>
            )}
            {showActionsForInternalUsers ? (
              <DocumentsValidationCreditAnalystAndManagerDecisions
                contractStatus={contractStatus}
                hasDocuments={!!files?.filter(f => phase3FileTypes.includes(f.documentType)).length}
              />
            ) : null}
          </div>
        </>
      )}
      <ConditionalsAndExceptions />
      <div className="flex flex-column">
        {draftFiles?.map(file => (
          <div key={file.id} className="flex flex-column">
            <TinyTitle className="text-transform-capitalize" style={{ marginBottom: 4 }}>
              {documentRequestTypesTranslations[file.documentType]}
            </TinyTitle>
            <DocumentCard document={file} />
          </div>
        ))}
      </div>
    </NotificationSection>
  );
};
